import React from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  CheckCircle,
  Award,
  Globe,
  Users,
  TrendingUp,
  FileCheck,
  ArrowRight,
  Mail,
  ExternalLink,
  Target,
  BarChart3,
} from "lucide-react";
import Navbar from "../../../components/Navbar";
import Footer from "../../../components/Footer";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 },
};

const SOCType2_Details = () => {
  return (
    <>
      <Navbar />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white py-24 px-4 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-orange-500 rounded-full blur-3xl" />
          <div className="absolute bottom-10 right-10 w-96 h-96 bg-purple-600 rounded-full blur-3xl" />
        </div>
        <motion.div
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          transition={{ duration: 0.7 }}
          className="relative max-w-5xl mx-auto text-center"
        >
          <div className="inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full text-sm mb-6">
            <ShieldCheck size={18} className="text-orange-400" />
            <span>ISO Lead Auditor Training</span>
          </div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
            SOC Type 2 Certification
          </h1>
          <p className="text-lg md:text-xl text-gray-300 max-w-3xl mx-auto leading-relaxed">
            System and Organization Controls Type 2 — Comprehensive audit of
            control effectiveness over time (6–12 months) for security,
            availability, confidentiality, processing integrity, and privacy.
          </p>
        </motion.div>
      </section>

      {/* Overview */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
              SOC Type 2 Certification Overview
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-4">
              SOC Type 2 (System and Organization Controls Type 2) is a
              comprehensive audit report that evaluates the effectiveness of an
              organization’s internal controls related to security,
              availability, confidentiality, and processing integrity over a
              specified period of time (usually 6 to 12 months). Unlike SOC Type
              1, which focuses on the design and implementation of controls at a
              specific point in time, SOC Type 2 assesses how well the controls
              operate on an ongoing basis, providing assurance that they are
              consistently functioning as intended.
            </p>
            <p className="text-gray-600 text-lg leading-relaxed">
              SOC Type 2 certification is essential for service organizations
              that handle sensitive data, provide outsourced services, or rely
              on third-party systems to ensure the security and privacy of their
              clients’ information. It is often required by clients, investors,
              and regulatory bodies to ensure that service providers are
              maintaining robust security practices and can meet business
              continuity requirements over time.
            </p>
          </motion.div>
        </div>
      </section>

      {/* What Is */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Is SOC Type 2 Certification?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-8">
              SOC Type 2 is an audit report that evaluates the effectiveness of
              controls over a specified period (typically 6-12 months). This
              report focuses on the operation and sustainability of the controls
              put in place by a service organization, ensuring that they are
              consistently providing the intended results over time.
            </p>
            <p className="text-gray-700 font-semibold text-lg mb-4">
              Key aspects covered in a SOC Type 2 report include:
            </p>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {[
                "Security: Ensuring that the service organization has appropriate controls to protect data confidentiality, integrity, and availability.",
                "Availability: Verifying that systems are consistently available for the service’s intended use, minimizing downtime and disruptions.",
                "Confidentiality: Ensuring that the organization’s controls are preventing unauthorized access to sensitive data.",
                "Processing Integrity: Ensuring the accuracy, completeness, and validity of data processing.",
              ].map((text, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.1 }}
                  className="flex items-center gap-3 bg-white p-4 rounded-xl shadow-sm border border-gray-100"
                >
                  <CheckCircle
                    size={22}
                    className="text-[var(--bg-primary)] shrink-0"
                  />
                  <span className="text-gray-700 font-medium">{text}</span>
                </motion.div>
              ))}
            </div>
            <p className="text-gray-600 text-lg leading-relaxed mt-6">
              A SOC Type 2 report provides third-party validation of the
              controls in place and provides customers with the assurance that
              these controls are effective over an extended period.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Key Features */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Key Features of SOC Type 2 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mx-auto" />
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              {
                icon: ShieldCheck,
                title: "Ongoing Control Effectiveness",
                desc: "Unlike SOC Type 1, which evaluates controls at a single point in time, SOC Type 2 evaluates how well controls function over a specified period (e.g., 6 or 12 months). It assures stakeholders that the organization’s controls are consistently effective in mitigating risk and ensuring security, privacy, and availability.",
              },
              {
                icon: Globe,
                title: "Comprehensive Coverage of Trust Services Criteria",
                desc: "SOC Type 2 reports assess controls based on five Trust Services Criteria: Security (protection of systems and data from unauthorized access and attacks), Availability (ensuring that systems are operational and accessible as needed), Confidentiality (ensuring sensitive data is kept private), Processing Integrity (ensuring that data processing is accurate, complete, and reliable), and Privacy (ensuring that personal data is collected, stored, and processed in compliance with privacy regulations).",
              },
              {
                icon: Award,
                title: "Third-Party Auditing",
                desc: "SOC Type 2 reports are issued by independent third-party auditors who evaluate the design, implementation, and operational effectiveness of an organization’s controls. This external validation provides objectivity and credibility to the report, increasing trust with stakeholders.",
              },
              {
                icon: Users,
                title: "Assurance to Clients and Stakeholders",
                desc: "The SOC Type 2 report provides organizations’ customers, investors, and regulators with the assurance that security controls are functioning as intended over a period of time. This builds confidence in the organization’s ability to protect sensitive data and meet compliance requirements.",
              },
            ].map((feature, i) => (
              <motion.div
                key={i}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeUp}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                className="group bg-gray-50 hover:bg-white p-6 rounded-2xl border border-gray-100 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-12 h-12 bg-[var(--bg-primary)] rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform">
                  <feature.icon size={24} className="text-white" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-2">
                  {feature.title}
                </h3>
                <p className="text-gray-600">{feature.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Matters */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Why SOC Type 2 Certification Matters for Your Business
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {[
                "Demonstrates Ongoing Control Effectiveness — SOC Type 2 certification provides evidence that your security controls are not only in place but are working effectively over time, reducing the likelihood of incidents such as data breaches, system downtimes, or loss of confidentiality.",
                "Builds Trust with Customers and Stakeholders — Achieving SOC Type 2 certification demonstrates to your clients and stakeholders that you maintain high standards for data security, privacy, and service availability. It helps build trust and reassures customers that their sensitive data is in safe hands.",
                "Compliance with Industry Regulations — SOC Type 2 certification helps organizations meet various regulatory requirements related to data protection, privacy, and information security, including GDPR, HIPAA, PCI DSS, and other industry-specific standards. The certification ensures that organizations are consistently compliant with the security requirements defined by regulators.",
                "Competitive Advantage — Achieving SOC Type 2 certification provides a competitive advantage in the marketplace, particularly for businesses in financial services, cloud computing, IT outsourcing, and other industries where security, availability, and data confidentiality are paramount.",
                "Risk Mitigation — By ensuring that internal controls are consistently effective, SOC Type 2 helps organizations proactively mitigate risks related to data breaches, service disruptions, and security vulnerabilities.",
              ].map((point, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="flex items-start gap-3 p-4 rounded-xl bg-white border border-gray-100"
                >
                  <ArrowRight
                    size={18}
                    className="text-[var(--bg-primary)] mt-1 shrink-0"
                  />
                  <span className="text-gray-700">{point}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* What Does Include */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              What Does SOC Type 2 Certification Include?
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-600 text-lg leading-relaxed mb-6">
              SOC Type 2 certification evaluates the effectiveness of the
              service organization’s controls over a specified period. The key
              components include:
            </p>
            <div className="space-y-4">
              {[
                "1. Control Environment Assessment — The SOC Type 2 audit assesses how well the organization’s controls align with Trust Services Criteria (security, availability, confidentiality, processing integrity, and privacy). This includes evaluating how the organization has implemented controls to mitigate risks and ensure effective protection of systems and data.",
                "2. Period of Review — SOC Type 2 reports assess controls over a specified period (usually 6 to 12 months). The audit evaluates the consistent performance of controls during this timeframe, ensuring that controls are operating effectively and meeting the defined objectives.",
                "3. Control Documentation and Reporting — The audit evaluates documentation and evidence that demonstrates the operation and effectiveness of controls over the review period. The SOC Type 2 report details the auditor’s findings and provides an independent evaluation of control effectiveness.",
                "4. Third-Party Assurance — The SOC Type 2 report is issued by an independent third-party auditor, providing external validation of the organization’s controls. The third-party auditor reviews the organization’s control environment and provides a formal report on control effectiveness.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 px-4 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              Benefits of SOC Type 2 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {[
                "Improved Control Effectiveness",
                "Trust and Confidence",
                "Regulatory Compliance",
                "Competitive Advantage",
                "Risk Mitigation and Incident Prevention",
              ].map((adv, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.05 }}
                  className="bg-white p-5 rounded-2xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                >
                  <CheckCircle
                    size={20}
                    className="text-[var(--bg-primary)] mb-3"
                  />
                  <p className="text-gray-700 font-medium text-sm">{adv}</p>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* How to Achieve */}
      <section className="py-16 px-4 bg-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">
              How to Achieve SOC Type 2 Certification
            </h2>
            <div className="w-20 h-1 bg-[var(--bg-primary)] rounded-full mb-8" />
            <p className="text-gray-700 font-semibold text-lg mb-6">
              To achieve SOC Type 2 certification, organizations typically
              follow these steps:
            </p>
            <div className="space-y-4">
              {[
                "1. Define Security and Control Requirements — Establish and implement the security controls and processes that meet the requirements of the Trust Services Criteria (security, availability, confidentiality, processing integrity, and privacy).",
                "2. Prepare for the Audit — Review your control environment and ensure that all relevant documentation and evidence are in place to demonstrate the effectiveness of your controls during the audit period.",
                "3. Conduct the SOC Type 2 Audit — Engage an accredited third-party auditor to assess the design, implementation, and operational effectiveness of your security controls over a specified period (e.g., 6-12 months).",
                "4. Audit Report Review — The auditor will provide an independent SOC Type 2 report, detailing the effectiveness of your controls and providing assurance to stakeholders that security, availability, confidentiality, and privacy requirements have been consistently met.",
                "5. Continuous Monitoring and Improvement — Implement a process for continuous monitoring of your security controls to ensure that they remain effective over time. Regularly review and improve your controls to adapt to emerging risks and changing business needs.",
              ].map((step, i) => (
                <motion.div
                  key={i}
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={fadeUp}
                  transition={{ duration: 0.4, delay: i * 0.08 }}
                  className="flex items-start gap-4 bg-gray-50 p-5 rounded-2xl shadow-sm border border-gray-100"
                >
                  <div className="w-8 h-8 bg-[var(--bg-primary)] rounded-full flex items-center justify-center shrink-0">
                    <span className="text-white font-bold text-sm">
                      {i + 1}
                    </span>
                  </div>
                  <span className="text-gray-700 text-lg">{step}</span>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Why Choose MBA Solutions */}
      <section className="py-16 px-4 bg-gradient-to-br from-[#1a1a2e] via-[#16213e] to-[#0f3460] text-white">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6 }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              Why Choose MBA Solutions for SOC Type 2 Certification?
            </h2>
            <div className="w-20 h-1 bg-orange-500 rounded-full mx-auto mb-6" />
            <p className="text-gray-300 text-lg leading-relaxed max-w-4xl mx-auto">
              MBA Solutions Pvt. Ltd. offers expert SOC Type 2 certification
              consultancy services, helping businesses implement robust security
              controls and data protection measures to ensure the security and
              availability of critical systems and data over time.
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-10"
          >
            <p className="text-gray-300 font-semibold text-lg text-center mb-6">
              Our services include:
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                "SOC Type 2 Readiness Assessment",
                "Control Implementation and Optimization",
                "Third-Party Audit Coordination",
                "Internal Audit and Pre-certification Support",
                "Continuous Monitoring and Risk Mitigation",
              ].map((service, i) => (
                <span
                  key={i}
                  className="bg-white/10 backdrop-blur-sm px-5 py-2 rounded-full text-sm font-medium"
                >
                  {service}
                </span>
              ))}
            </div>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeUp}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="bg-white/10 backdrop-blur-sm rounded-3xl p-8 md:p-10 text-center"
          >
            <div className="flex flex-col sm:flex-row items-center justify-center gap-6 mb-6">
              <a
                href="mailto:atif@mbasolutions.co"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <Mail size={20} />
                <span>atif@mbasolutions.co</span>
              </a>
              <a
                href="https://www.mbasolutions.co"
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 text-orange-400 hover:text-orange-300 transition-colors"
              >
                <ExternalLink size={20} />
                <span>www.mbasolutions.co</span>
              </a>
            </div>
            <p className="text-gray-300 text-base leading-relaxed max-w-3xl mx-auto">
              We help businesses achieve SOC Type 2 certification, ensuring
              robust control systems, data protection, and regulatory
              compliance.
            </p>
          </motion.div>
        </div>
      </section>

      <Footer />
    </>
  );
};

export default SOCType2_Details;
